<template>
  <component
    :is="href ? 'a' : 'div'"
    class="metric-tile"
    :class="{ 'metric-tile--linked': href }"
    :href="href || undefined"
    data-cy="metric-tile"
  >
    <div class="metric-tile__head">
      <span class="metric-tile__label">{{ label }}</span>
      <Tooltip
        v-if="tooltip"
        placement="top"
        :width="280"
        class="metric-tile__tooltip-wrap"
      >
        <template #trigger>
          <i
            class="fas fa-info-circle metric-tile__info"
            aria-label="More information"
          ></i>
        </template>
        <template #tooltip>
          <span class="metric-tile__tooltip-text">{{ tooltip }}</span>
        </template>
      </Tooltip>
    </div>
    <div class="metric-tile__body">
      <div class="metric-tile__value-row">
        <Skeleton
          v-if="showSkeleton"
          width="6rem"
          height="1.875rem"
          :rounded="false"
          data-cy="metric-tile-skeleton"
        />
        <span
          v-else
          class="metric-tile__value"
          :class="[
            `metric-tile__value--${valueTone}`,
            severity !== 'none' ? `metric-tile__value--${severity}` : null
          ]"
          data-cy="metric-tile-value"
        >
          {{ value }}
        </span>
      </div>
      <span v-if="helperText" class="metric-tile__helper">
        {{ helperText }}
      </span>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import Skeleton from "./Skeleton.vue";

const props = withDefaults(
  defineProps<{
    label: string;
    value?: string | number | null;
    tooltip?: string;
    helperText?: string;
    loading?: boolean;
    href?: string;
    valueTone?: "positive" | "negative" | "neutral";
    /**
     * Urgency signal for at-risk and action-item metrics. Visually
     * overrides `valueTone` when set to anything other than "none".
     * Use "caution" for action items where any non-zero count is work
     * to do (amber). Use "alert" for at-risk metrics where higher
     * counts deserve more attention (red). Caller decides the threshold.
     */
    severity?: "none" | "caution" | "alert";
  }>(),
  {
    value: null,
    tooltip: "",
    helperText: "",
    loading: false,
    href: "",
    valueTone: "neutral",
    severity: "none"
  }
);

const showSkeleton = computed(
  () => props.loading || props.value === null || props.value === undefined
);
</script>

<style lang="scss" scoped>
.metric-tile {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.25rem;
  min-height: 6.5rem;
  text-decoration: none;
  color: inherit;
}
.metric-tile__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.375rem;
}
.metric-tile--linked {
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &:hover {
    border-color: #9ca3af;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }
  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
}
.metric-tile__head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.metric-tile__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4b5563;
}
.metric-tile__info {
  font-size: 0.75rem;
  color: #9ca3af;
  cursor: help;
  transition: color 120ms ease;
  &:hover {
    color: #4b5563;
  }
}
.metric-tile__tooltip-wrap {
  display: inline-flex;
  align-items: center;
}
.metric-tile__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.metric-tile__value-row {
  display: flex;
  align-items: baseline;
  min-height: 1.875rem;
}
.metric-tile__value {
  font-size: 1.875rem;
  font-weight: 600;
  line-height: 1.1;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.metric-tile__value--positive {
  color: #047857;
}
.metric-tile__value--negative {
  color: #b91c1c;
}
.metric-tile__value--caution {
  color: #b45309;
}
.metric-tile__value--alert {
  color: #b91c1c;
}
.metric-tile__helper {
  font-size: 0.75rem;
  color: #6b7280;
}
</style>
